import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import RemoveIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/EditOutlined";

import { useDatagridContextValue } from "../../../../context/datagridContext";
import {
  formConditional,
  getWatchFieldName,
} from "../../../../services/form.service";
import { getMaxAllowedRecordCount } from "../../../../services/form.datagrid.service";

function ActionButtons(props) {
  const {
    record,
    records,
    gridItem,
    recordIndex,
    dgConfigRef,
    handleSubmit,
    isCategorizedView,
    handleAppendRecord,
    handleRemoveRecord,
    handleEditRecord,
    handleCancelAction,
  } = props;
  const { getValues, control } = useFormContext();
  const { datagridFormState } = useDatagridContextValue();
  const formData = useSelector((state) => state.formProps.formData);

  const currentRecordsCount = records.length;
  const isLastRecord = currentRecordsCount === recordIndex + 1;
  const { maxRecordCount, allowedActions, conditional, conditionalLimit } =
    dgConfigRef.current || {};

  const { add, edit, remove } = conditional || {};
  const addFieldProps = { key: `${gridItem.key}AddBtn`, conditional: add };
  const editFieldProps = { key: `${gridItem.key}EditBtn`, conditional: edit };
  const removeFieldProps = {
    key: `${gridItem.key}RemoveBtn`,
    conditional: remove,
  };
  const addBtnWatchFieldValue = useWatch({
    name: getWatchFieldName(addFieldProps),
    disabled: !add?.when,
    control,
  });

  const editBtnWatchFieldValue = useWatch({
    name: getWatchFieldName(editFieldProps),
    disabled: !edit?.when,
    control,
  });

  const removeBtnWatchFieldValue = useWatch({
    name: getWatchFieldName(removeFieldProps),
    disabled: !remove?.when,
    control,
  });

  const shouldDisplay = (fieldProps, _watchFieldValue) => {
    let watchFieldValue = _watchFieldValue;
    if (
      fieldProps.conditional &&
      watchFieldValue === undefined &&
      formData[fieldProps.conditional.when] !== undefined
    ) {
      watchFieldValue = formData[fieldProps.conditional.when];
    }
    return formConditional(fieldProps, watchFieldValue, getValues());
  };

  const displayAddButtonConditionally = shouldDisplay(
    addFieldProps,
    addBtnWatchFieldValue
  );
  const displayEditButtonConditionally = shouldDisplay(
    editFieldProps,
    editBtnWatchFieldValue
  );
  const displayRemoveButtonConditionally = shouldDisplay(
    removeFieldProps,
    removeBtnWatchFieldValue
  );

  // to disable/enable "add new record" button based on condition
  const { condition = [] } = conditionalLimit || {};
  const disableFieldKeys =
    Array.isArray(condition) && condition.length
      ? condition.map(({ when }) => when)
      : null;

  const addButtonDisableValues = useWatch({
    control,
    name: disableFieldKeys,
    disabled: !disableFieldKeys,
    defaultValue: disableFieldKeys
      ? disableFieldKeys.map((key) => getValues(key))
      : [],
  });

  const maxRecordsAllowed = getMaxAllowedRecordCount(
    conditionalLimit,
    maxRecordCount,
    addButtonDisableValues
  );

  const addBtnDisabled = currentRecordsCount >= maxRecordsAllowed;
  const { isEditable } = datagridFormState || {};

  const showRemoveBtn =
    displayRemoveButtonConditionally &&
    ((isEditable && record.status !== "saved") ||
      (!isEditable && record.status === "saved")) &&
    (currentRecordsCount > 1 || gridItem.initEmpty) &&
    (!allowedActions || allowedActions.includes("remove"));

  const showEditBtn =
    displayEditButtonConditionally &&
    !isEditable &&
    (!allowedActions || allowedActions.includes("edit"));

  const showCancelBtn = isEditable && record.status === "saved";

  const showAddBtn =
    displayAddButtonConditionally &&
    !isCategorizedView &&
    !isEditable &&
    isLastRecord &&
    // (!maxRecordsAllowed || maxRecordsAllowed > currentRecordsCount) &&
    (!allowedActions || allowedActions.includes("add"));

  const addBtnTooTipMsg = `Number of entries ${
    addBtnDisabled && currentRecordsCount > maxRecordsAllowed
      ? "exceeded"
      : "reached"
  } the maximum limit of ${maxRecordsAllowed}`;

  return (
    <Grid container className="gridContainer">
      {showRemoveBtn && (
        <Button
          onClick={() => handleRemoveRecord()}
          startIcon={<RemoveIcon />}
          color="error"
        >
          Remove Record
        </Button>
      )}
      {showEditBtn && (
        <Button onClick={handleEditRecord} startIcon={<EditIcon />}>
          Edit Record
        </Button>
      )}
      {showCancelBtn && (
        <Button
          onClick={handleCancelAction}
          startIcon={<BlockIcon sx={{ fontSize: "15px !important" }} />}
          color="error"
        >
          Cancel
        </Button>
      )}
      {isEditable && (
        <Button
          variant="outlined"
          onClick={handleSubmit}
          startIcon={<SaveIcon />}
          className="saveBtnOutlined"
        >
          Save Record
        </Button>
      )}
      {showAddBtn && (
        <Tooltip title={addBtnDisabled ? addBtnTooTipMsg : ""}>
          <span>
            <Button
              onClick={handleAppendRecord}
              startIcon={<AddIcon />}
              disabled={addBtnDisabled}
              sx={({ palette: { mode } }) => ({
                "&.Mui-disabled": {
                  color: mode === "dark" ? "#f6f2f2ba" : "#000000ad",
                },
              })}
            >
              Add New Record
            </Button>
          </span>
        </Tooltip>
      )}
    </Grid>
  );
}

export default ActionButtons;
ActionButtons.propTypes = {
  gridItem: PropTypes.shape({
    key: PropTypes.string.isRequired,
    components: PropTypes.oneOfType([PropTypes.array]).isRequired,
    initEmpty: PropTypes.bool,
  }).isRequired,
  record: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
  }).isRequired,
  isCategorizedView: PropTypes.bool.isRequired,
  recordIndex: PropTypes.number.isRequired,
  handleAppendRecord: PropTypes.func.isRequired,
  handleEditRecord: PropTypes.func.isRequired,
  handleRemoveRecord: PropTypes.func.isRequired,
  handleCancelAction: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  records: PropTypes.oneOfType([PropTypes.array]).isRequired,
  dgConfigRef: PropTypes.shape({ current: PropTypes.shape({}) }).isRequired,
};
